/**
 * ContractorWorkOrdersBoard
 * -------------------------
 * Contractor-scoped lifecycle board. The contractor sees only WOs assigned
 * to them (auth-enforced server-side). Per WO they can:
 *   - View checklist + tick tasks off (auto-posts a "✓ Completed" milestone)
 *   - Post field updates with photos / attachments
 *   - See live completion % update as they work
 *
 * They never see supplier quotes or internal admin notes.
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  ArrowLeft,
  CheckCircle2,
  Circle,
  Loader2,
  Paperclip,
  Send,
  HardHat,
  Sparkles,
  RefreshCw,
  X,
  ChevronRight,
  AlertOctagon,
  Plus,
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const STATUS_COLORS = {
  pending: "bg-slate-100 text-slate-700",
  triage: "bg-slate-100 text-slate-700",
  assigned: "bg-blue-100 text-blue-700",
  in_progress: "bg-violet-100 text-violet-700",
  quotes_received: "bg-amber-100 text-amber-800",
  awaiting_hospital_signoff: "bg-amber-100 text-amber-800",
  closed: "bg-emerald-100 text-emerald-700",
  completed: "bg-emerald-100 text-emerald-700",
  disputed: "bg-rose-100 text-rose-700",
};

const fmt = (iso) => (iso ? new Date(iso).toLocaleString() : "");

export default function ContractorWorkOrdersBoard({ token }) {
  const headers = { Authorization: `Bearer ${token}` };
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchList = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/contractor/work-orders`, { headers });
      setItems(res.data?.items || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load work orders");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (selected) {
    return (
      <ContractorWorkOrderDetail
        woId={selected}
        token={token}
        onBack={() => {
          setSelected(null);
          fetchList();
        }}
      />
    );
  }

  return (
    <div className="space-y-4" data-testid="contractor-wo-board">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-slate-900">Active Work Orders</h2>
          <p className="text-sm text-slate-500">Open a WO to tick off tasks or post a field update</p>
        </div>
        <Button onClick={fetchList} variant="outline" size="sm" disabled={loading}>
          <RefreshCw className={`w-4 h-4 mr-1 ${loading ? "animate-spin" : ""}`} /> Refresh
        </Button>
      </div>

      {loading && items.length === 0 ? (
        <div className="flex items-center gap-2 text-slate-500 p-8">
          <Loader2 className="w-5 h-5 animate-spin" /> Loading…
        </div>
      ) : items.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-xl p-12 text-center">
          <HardHat className="w-12 h-12 text-slate-300 mx-auto mb-4" />
          <h3 className="text-slate-900 font-medium mb-2">No work orders assigned yet</h3>
          <p className="text-sm text-slate-500">
            When an admin assigns you, the WO will appear here with checklists and updates.
          </p>
        </div>
      ) : (
        <div className="space-y-2">
          {items.map((wo) => {
            const pct = wo.completion_pct ?? 0;
            const status = wo.status || "pending";
            return (
              <button
                key={wo.id}
                onClick={() => setSelected(wo.id)}
                className="w-full text-left rounded-lg border border-slate-200 bg-white p-4 hover:border-blue-300 hover:shadow-sm transition-all"
                data-testid={`contractor-wo-row-${wo.id}`}
              >
                <div className="flex items-start justify-between gap-3 flex-wrap mb-2">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="font-semibold text-slate-900 truncate">{wo.title || wo.description}</h3>
                      <Badge className={STATUS_COLORS[status] || "bg-slate-100 text-slate-700"}>
                        {String(status).replace(/_/g, " ")}
                      </Badge>
                    </div>
                    <p className="text-xs text-slate-500 mt-0.5">
                      <span className="font-mono">{wo.id}</span>
                      {wo.hospital_name && ` · ${wo.hospital_name}`}
                      {wo.priority && ` · ${wo.priority}`}
                    </p>
                  </div>
                  <ChevronRight className="w-5 h-5 text-slate-400 flex-shrink-0" />
                </div>
                <div className="flex items-center gap-3 text-xs">
                  <div className="flex-1 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                    <div className="h-full bg-emerald-500 transition-all" style={{ width: `${pct}%` }} />
                  </div>
                  <span className="font-semibold text-emerald-700">{pct}%</span>
                  <span className="text-slate-400">
                    {wo.checklist?.filter((t) => t.completed).length || 0}/{wo.checklist?.length || 0} tasks
                  </span>
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ----------------------------------------------------------------
// Detail view
// ----------------------------------------------------------------
function ContractorWorkOrderDetail({ woId, token, onBack }) {
  const headers = { Authorization: `Bearer ${token}` };
  const [wo, setWo] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchWo = async () => {
    try {
      const res = await axios.get(`${API}/contractor/work-orders/${woId}`, { headers });
      setWo(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    fetchWo();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [woId]);

  if (loading || !wo) {
    return (
      <div className="flex items-center gap-2 text-slate-500 p-8">
        <Loader2 className="w-5 h-5 animate-spin" /> Loading…
      </div>
    );
  }

  const pct = wo.completion_pct ?? 0;
  const status = wo.status || "pending";

  return (
    <div className="space-y-4 max-w-3xl">
      <Button variant="ghost" onClick={onBack} className="-ml-2">
        <ArrowLeft className="w-4 h-4 mr-1" /> Back to work orders
      </Button>

      <div className="bg-white rounded-xl border border-slate-200 p-5">
        <div className="flex items-start justify-between gap-3 flex-wrap mb-3">
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className="text-xl font-bold text-slate-900">{wo.title || wo.description}</h1>
              <Badge className={STATUS_COLORS[status] || "bg-slate-100 text-slate-700"}>
                {String(status).replace(/_/g, " ")}
              </Badge>
            </div>
            <p className="text-xs text-slate-500 mt-1 font-mono">{wo.id}</p>
            {wo.hospital_name && <p className="text-sm text-slate-700 mt-1">{wo.hospital_name}</p>}
          </div>
          <div className="text-right">
            <div className="text-2xl font-bold text-emerald-600">{pct}%</div>
            <div className="w-32 h-2 bg-slate-100 rounded-full overflow-hidden">
              <div className="h-full bg-emerald-500" style={{ width: `${pct}%` }} />
            </div>
          </div>
        </div>
      </div>

      <ContractorChecklist wo={wo} token={token} onChange={fetchWo} />
      <ContractorUpdates wo={wo} token={token} onChange={fetchWo} />
      <ContractorReportIncident wo={wo} token={token} />
    </div>
  );
}

function ContractorChecklist({ wo, token, onChange }) {
  const headers = { Authorization: `Bearer ${token}` };
  const items = wo.checklist || [];
  const [busyId, setBusyId] = useState(null);

  const toggle = async (task) => {
    setBusyId(task.id);
    try {
      await axios.patch(
        `${API}/contractor/work-orders/${wo.id}/checklist/${task.id}`,
        { completed: !task.completed },
        { headers }
      );
      if (!task.completed) toast.success(`✓ ${task.label}`);
      onChange();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to update");
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="bg-white rounded-xl border border-slate-200 p-5">
      <div className="flex items-center justify-between mb-3">
        <h2 className="font-semibold text-slate-900 flex items-center gap-2">
          <CheckCircle2 className="w-4 h-4 text-emerald-600" /> Task Checklist
        </h2>
        <span className="text-xs text-slate-500">
          {items.filter((i) => i.completed).length}/{items.length} done
        </span>
      </div>
      {items.length === 0 ? (
        <p className="text-xs text-slate-500 italic">
          The admin hasn&apos;t set up tasks yet for this work order.
        </p>
      ) : (
        <ul className="space-y-1.5">
          {items.map((task) => (
            <li
              key={task.id}
              className={`flex items-center gap-2 rounded border p-2.5 text-sm ${
                task.completed ? "border-emerald-200 bg-emerald-50/40" : "border-slate-200"
              }`}
              data-testid={`contractor-task-${task.id}`}
            >
              <button
                onClick={() => toggle(task)}
                disabled={busyId === task.id}
                className="flex-shrink-0"
              >
                {busyId === task.id ? (
                  <Loader2 className="w-5 h-5 animate-spin text-emerald-600" />
                ) : task.completed ? (
                  <CheckCircle2 className="w-5 h-5 text-emerald-600" />
                ) : (
                  <Circle className="w-5 h-5 text-slate-300" />
                )}
              </button>
              <span className={`flex-1 ${task.completed ? "line-through text-slate-400" : "text-slate-900"}`}>
                {task.label}
              </span>
              {task.completed && task.completed_by && (
                <span className="text-[10px] text-slate-400">{task.completed_by}</span>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function ContractorUpdates({ wo, token, onChange }) {
  const headers = { Authorization: `Bearer ${token}` };
  const [message, setMessage] = useState("");
  const [attachments, setAttachments] = useState([]);
  const [busy, setBusy] = useState(false);
  const updates = wo.updates || [];

  const handleFile = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (files.length === 0) return;
    setBusy(true);
    for (const file of files) {
      const form = new FormData();
      form.append("file", file);
      try {
        const res = await axios.post(
          `${API}/contractor/work-orders/${wo.id}/updates/upload`,
          form,
          { headers: { ...headers, "Content-Type": "multipart/form-data" } }
        );
        setAttachments((p) => [...p, res.data]);
      } catch (e) {
        toast.error(`${file.name}: ${e.response?.data?.detail || e.message}`);
      }
    }
    setBusy(false);
  };

  const post = async () => {
    if (!message.trim() && attachments.length === 0) {
      toast.error("Add a message or attach a photo");
      return;
    }
    setBusy(true);
    try {
      await axios.post(
        `${API}/contractor/work-orders/${wo.id}/updates`,
        {
          message: message || `Attached ${attachments.length} file(s)`,
          attachments,
          photos: attachments
            .filter((a) => /\.(png|jpe?g|webp|gif|heic)$/i.test(a.file_name || ""))
            .map((a) => a.file_url),
        },
        { headers }
      );
      setMessage("");
      setAttachments([]);
      toast.success("Update posted — hospital & admin notified");
      onChange();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to post");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="bg-white rounded-xl border border-slate-200 p-5">
      <h2 className="font-semibold text-slate-900 flex items-center gap-2 mb-3">
        <Sparkles className="w-4 h-4 text-blue-600" /> Field Updates
      </h2>

      {/* Composer */}
      <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 mb-4 space-y-2" data-testid="contractor-update-composer">
        <Textarea
          rows={3}
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          placeholder="What did you find / do? (visible to hospital + admin)"
          data-testid="contractor-update-input"
        />
        {attachments.length > 0 && (
          <div className="flex flex-wrap gap-1.5">
            {attachments.map((a, i) => (
              <Badge key={i} variant="outline" className="gap-1 text-[11px] pr-1">
                <Paperclip className="w-2.5 h-2.5" />
                <span className="truncate max-w-[160px]">{a.file_name}</span>
                <button onClick={() => setAttachments((p) => p.filter((_, x) => x !== i))} className="hover:text-rose-500">
                  <X className="w-3 h-3" />
                </button>
              </Badge>
            ))}
          </div>
        )}
        <div className="flex items-center gap-2 flex-wrap">
          <label className="inline-flex items-center gap-1.5 h-8 px-3 text-xs font-medium rounded border border-slate-300 bg-white hover:bg-slate-50 cursor-pointer">
            <Paperclip className="w-3 h-3" /> Add photo / file
            <input
              type="file"
              multiple
              accept=".pdf,.png,.jpg,.jpeg,.webp,.heic,.gif,.mp4,.mov"
              className="hidden"
              onChange={handleFile}
              disabled={busy}
              data-testid="contractor-update-attach-input"
            />
          </label>
          <div className="text-[11px] text-slate-500 italic">Photos shown to hospital in their portal</div>
          <Button
            onClick={post}
            disabled={busy}
            className="bg-blue-600 hover:bg-blue-700 ml-auto"
            data-testid="contractor-update-post-btn"
          >
            {busy ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Send className="w-3 h-3 mr-1" />}
            Post update
          </Button>
        </div>
      </div>

      {/* Feed */}
      {updates.length === 0 ? (
        <p className="text-xs text-slate-500 italic">No updates yet — be the first.</p>
      ) : (
        <ul className="space-y-2.5">
          {[...updates].reverse().map((u) => (
            <li key={u.id} className="rounded-lg border border-slate-200 bg-white p-3" data-testid={`contractor-update-${u.id}`}>
              <div className="flex items-center gap-2 text-xs text-slate-600 mb-1 flex-wrap">
                <span className="font-semibold text-slate-800">{u.by_name}</span>
                <span className="text-slate-400">{fmt(u.created_at)}</span>
                {u.update_type === "milestone" && (
                  <Badge className="bg-emerald-100 text-emerald-700 text-[10px] border-emerald-200">milestone</Badge>
                )}
              </div>
              <p className="text-sm text-slate-800 whitespace-pre-wrap">{u.message}</p>
              {Array.isArray(u.photos) && u.photos.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {u.photos.map((p, i) => (
                    <a key={i} href={`${API}${p}`} target="_blank" rel="noreferrer">
                      <img
                        src={`${API}${p}`}
                        alt=""
                        className="w-16 h-16 object-cover rounded border border-slate-200"
                      />
                    </a>
                  ))}
                </div>
              )}
              {Array.isArray(u.attachments) && u.attachments.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {u.attachments.map((a, i) => (
                    <a
                      key={i}
                      href={`${API}${a.file_url}`}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center gap-1 text-xs text-blue-600 hover:underline border border-slate-200 rounded px-2 py-1"
                    >
                      <Paperclip className="w-3 h-3" /> {a.file_name}
                    </a>
                  ))}
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}



// ----------------------------------------------------------------
// Contractor — Report Incident button
// ----------------------------------------------------------------
function ContractorReportIncident({ wo, token }) {
  const headers = { Authorization: `Bearer ${token}` };
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    title: "",
    description: "",
    occurred_at: new Date().toISOString().slice(0, 16),
  });

  const submit = async () => {
    if (!form.title.trim()) {
      toast.error("Add a title");
      return;
    }
    setSaving(true);
    try {
      const r = await axios.post(
        `${API}/contractor/work-orders/${wo.id}/incidents`,
        {
          title: form.title.trim(),
          description: form.description.trim() || undefined,
          occurred_at: form.occurred_at ? new Date(form.occurred_at).toISOString() : undefined,
        },
        { headers },
      );
      toast.success("Incident logged — admin + hospital notified");
      setOpen(false);
      setForm({ title: "", description: "", occurred_at: new Date().toISOString().slice(0, 16) });
      if (r.data?.public_url) {
        window.open(r.data.public_url, "_blank");
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to log incident");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-white rounded-xl border border-rose-200 p-4">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <AlertOctagon className="w-4 h-4 text-rose-500" />
          <div>
            <h3 className="font-semibold text-slate-900 text-sm">Report an incident</h3>
            <p className="text-xs text-slate-500">
              Hit a snag on site? Log it once — Sterile Mate and the hospital are notified.
            </p>
          </div>
        </div>
        {!open && (
          <Button
            size="sm"
            variant="outline"
            onClick={() => setOpen(true)}
            className="h-7"
            data-testid="ctr-incidents-toggle"
          >
            <Plus className="w-3.5 h-3.5 mr-1" /> Log incident
          </Button>
        )}
      </div>
      {open && (
        <div className="mt-3 space-y-2 border-t border-slate-100 pt-3">
          <div>
            <Label className="text-xs">Title *</Label>
            <Input
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              placeholder="e.g. Hidden pipe leak behind drywall"
              data-testid="ctr-incidents-title"
            />
          </div>
          <div>
            <Label className="text-xs">Date & time</Label>
            <Input
              type="datetime-local"
              value={form.occurred_at}
              onChange={(e) => setForm({ ...form, occurred_at: e.target.value })}
            />
          </div>
          <div>
            <Label className="text-xs">What happened?</Label>
            <Textarea
              rows={3}
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              placeholder="Describe the issue, anyone affected, parts/help you need."
              data-testid="ctr-incidents-description"
            />
          </div>
          <div className="flex gap-2 justify-end">
            <Button size="sm" variant="outline" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button
              size="sm"
              onClick={submit}
              disabled={saving}
              className="bg-rose-600 hover:bg-rose-700"
              data-testid="ctr-incidents-submit"
            >
              {saving && <Loader2 className="w-3 h-3 animate-spin mr-1" />}
              Submit & notify
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
